/* global gdAlertify */
import React, { useState } from "react";
import axiosInstance from "../config/axiosConfig";
import BuscarPaciente from "./components/BuscarPaciente";
import BuscarAuxiliar from "./components/BuscarAuxiliar";
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from "../utils/utils";

const normalizeMonto = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

function SolicitudReemplazo2({ onCreado } = {}) {
    const navigate = useNavigate();

    const [pacienteInfo, setPacienteInfo] = useState(null);
    const [auxiliarInfo, setAuxiliarInfo] = useState(null);
    const [auxiliares, setAuxiliares] = useState([]);

    // State management identical to original for compatibility
    const [datosCondiciones, setDatosCondiciones] = useState({
        pacienteID: 0,
        auxiliarID: 0,
        valorTurno: 0,
        valorColacion: 0,
        valorMovilizacion: 0,
        valorOtro: 0,
        fechaInicio: '',
        fechaFin: '',
        turnoInicio: 'day',
        turnoFin: 'night',
        turnoID: 0
    });

    const handleSeleccionPaciente = (paciente) => {
        if (paciente.valoresTurnos && paciente.valoresTurnos.length > 0) {
            const { valorTurnoAuxiliar, valorColacionAuxiliar, valorLocomocionAuxiliar, valorOtroAuxiliar } = paciente.valoresTurnos[0];
            setDatosCondiciones(prev => ({
                ...prev,
                valorTurno: valorTurnoAuxiliar,
                valorColacion: valorColacionAuxiliar,
                valorMovilizacion: valorLocomocionAuxiliar,
                valorOtro: valorOtroAuxiliar,
                pacienteID: paciente.id,
                auxiliarID: 0, // Reset auxiliar if patient changes
                turnoID: paciente.tipoTurno
            }));
        } else {
            setDatosCondiciones(prev => ({
                ...prev,
                pacienteID: paciente.id,
                auxiliarID: 0,
                turnoID: paciente.tipoTurno
            }));
        }

        setPacienteInfo(paciente);
        setAuxiliarInfo(null); // Reset aux info
        setAuxiliares(paciente.auxiliares.map(auxiliar => ({
            value: auxiliar.id,
            label: `${auxiliar.nombre} ${auxiliar.apellidoPaterno} ${auxiliar.apellidoMaterno} (${auxiliar.rut})`
        })));
    };

    const handleSeleccionAuxiliar = (auxiliar) => {
        setDatosCondiciones(prev => ({ ...prev, auxiliarID: auxiliar.id }));
        setAuxiliarInfo(auxiliar);

        // Logic from original: Auto-fill patient if linked
        if (auxiliar.paciente != null) {
            const { valorTurnoAuxiliar, valorColacionAuxiliar, valorLocomocionAuxiliar, valorOtroAuxiliar } = auxiliar.paciente.valoresTurnos[0];
            setDatosCondiciones({
                valorTurno: valorTurnoAuxiliar,
                valorColacion: valorColacionAuxiliar,
                valorMovilizacion: valorLocomocionAuxiliar,
                valorOtro: valorOtroAuxiliar,
                pacienteID: auxiliar.paciente.id,
                auxiliarID: auxiliar.id,
                fechaInicio: datosCondiciones.fechaInicio, // Keep dates if set
                fechaFin: datosCondiciones.fechaFin,
                turnoInicio: datosCondiciones.turnoInicio,
                turnoFin: datosCondiciones.turnoFin,
                turnoID: auxiliar.paciente.tipoTurno
            });
            setPacienteInfo(auxiliar.paciente);
            setAuxiliares(auxiliar.paciente.auxiliares.map(aux => ({
                value: aux.id,
                label: `${aux.nombre} ${aux.apellidoPaterno} ${aux.apellidoMaterno} (${aux.rut})`
            })));
        } else {
            // Manual add to list
            setAuxiliares(prev => {
                const existe = prev.some(a => a.value === auxiliar.id);
                if (!existe) {
                    return [...prev, {
                        value: auxiliar.id,
                        label: `${auxiliar.nombre} ${auxiliar.apellidoPaterno} ${auxiliar.apellidoMaterno} (${auxiliar.rut})`
                    }];
                }
                return prev;
            });
        }
    };

    const handleChangeCondiciones = (e) => {
        const { name, value } = e.target;
        setDatosCondiciones({ ...datosCondiciones, [name]: value });
    };

    const handleAuxiliarChange = (selectedOption) => {
        setDatosCondiciones({ ...datosCondiciones, auxiliarID: selectedOption ? selectedOption.value : "" });

        if (selectedOption && pacienteInfo && pacienteInfo.auxiliares) {
            const fullAux = pacienteInfo.auxiliares.find(a => a.id === selectedOption.value);
            if (fullAux) {
                setAuxiliarInfo(fullAux);
            }
        } else if (!selectedOption) {
            setAuxiliarInfo(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const fechaInicio = new Date(datosCondiciones.fechaInicio + 'T00:00:00');
        const fechaFin = new Date(datosCondiciones.fechaFin + 'T00:00:00');

        if (fechaFin < fechaInicio) {
            gdAlertify.notiError('La fecha de fin debe ser mayor o igual a la fecha de inicio.');
            return false;
        }

        if (
            datosCondiciones.fechaInicio === datosCondiciones.fechaFin &&
            datosCondiciones.turnoInicio === 'night' &&
            datosCondiciones.turnoFin === 'day'
        ) {
            gdAlertify.notiError('En la misma fecha no puede iniciar en noche y terminar en día.');
            return false;
        }

        if (!datosCondiciones.pacienteID) {
            gdAlertify.notiError('Debe seleccionar un Paciente.');
            return false;
        }

        if (!datosCondiciones.auxiliarID) {
            gdAlertify.notiError('Falta seleccionar auxiliar a reemplazar.');
            return false;
        }

        gdAlertify.confirm({
            title: 'Registrar solicitud',
            message: '¿Está seguro de registrar esta solicitud de reemplazo?',
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const payload = {
                        ...datosCondiciones,
                        valorTurno: normalizeMonto(datosCondiciones.valorTurno),
                        valorColacion: normalizeMonto(datosCondiciones.valorColacion),
                        valorMovilizacion: normalizeMonto(datosCondiciones.valorMovilizacion),
                        valorOtro: normalizeMonto(datosCondiciones.valorOtro),
                    };

                    const response = await axiosInstance.post("/reemplazos/solicitud/crear", {
                        datosCondiciones: payload,
                    });

                    if (!response.data.error) {
                        gdAlertify.notiSuccess(response.data.message);
                        if (onCreado) onCreado();
                        else navigate('/solicitudes-reemplazos');
                    } else {
                        gdAlertify.notiError(response.data.message);
                    }
                    gdAlertify.hideLoader();
                } catch (error) {
                    gdAlertify.hideLoader();
                    console.error(error);
                    gdAlertify.notiError("Ocurrió un error al conectar con el servidor.");
                }
            },
            onCancel: () => { }
        });
    };

    return (
        <div className="container py-5">
            {/* Header */}
            <div className="mb-4">
                <div className="d-flex align-items-center mb-2 text-muted">
                    <small className="text-uppercase fw-bold ls-1">Gestión de Reemplazos</small>
                    <span className="mx-2">•</span>
                    <small>Nueva Solicitud</small>
                </div>
                <h2 className="fw-bold text-dark mb-0">Crear Solicitud de Reemplazo</h2>
                <p className="text-secondary">Complete los datos para generar una nueva orden de reemplazo.</p>
            </div>

            <div className="row g-4">
                {/* Left Column: Form */}
                <div className="col-lg-8">
                    <form onSubmit={handleSubmit}>

                        {/* Step 1: Selection */}
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-header bg-white py-3">
                                <h5 className="mb-0 text-primary"><i className="fas fa-users me-2"></i>1. Selección de Involucrados</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-secondary text-uppercase small">Buscar Paciente</label>
                                        <div className="p-3 bg-light rounded border">
                                            <BuscarPaciente
                                                onSelect={handleSeleccionPaciente}
                                                valoresTurnos={true}
                                                conAuxiliares={true}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold text-secondary text-uppercase small">auxiliar (Titular)</label>
                                        <div className="p-3 bg-light rounded border">
                                            {/*  <BuscarAuxiliar
                                                titulo="Buscar por RUT/Nombre"
                                                onSelect={handleSeleccionAuxiliar}
                                                conPaciente={true}
                                            /> */}
                                            {/* Fallback dropdown if we have patient but need to select from their list */}
                                            <div className="mt-1">
                                                <label className="form-label small text-muted">Seleccionar de la lista del paciente:</label>
                                                <Select
                                                    options={auxiliares}
                                                    value={auxiliares.find(option => option.value === datosCondiciones.auxiliarID) || null}
                                                    onChange={handleAuxiliarChange}
                                                    placeholder="Seleccione auxiliar titular..."
                                                    isClearable
                                                    className="basic-single"
                                                    classNamePrefix="select"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 2: Date & Money */}
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-header bg-white py-3">
                                <h5 className="mb-0 text-primary"><i className="fas fa-calendar-alt me-2"></i>2. Fechas y Valores</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-4 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label">Fecha Inicio</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-lg"
                                            name="fechaInicio"
                                            value={datosCondiciones.fechaInicio}
                                            onChange={handleChangeCondiciones}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Turno Inicio</label>
                                        <select
                                            className="form-control form-control-lg"
                                            name="turnoInicio"
                                            value={datosCondiciones.turnoInicio}
                                            onChange={handleChangeCondiciones}
                                        >
                                            <option value="day">Día</option>
                                            <option value="night">Noche</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Fecha Fin</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-lg"
                                            name="fechaFin"
                                            value={datosCondiciones.fechaFin}
                                            onChange={handleChangeCondiciones}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Turno Fin</label>
                                        <select
                                            className="form-control form-control-lg"
                                            name="turnoFin"
                                            value={datosCondiciones.turnoFin}
                                            onChange={handleChangeCondiciones}
                                        >
                                            <option value="day">Día</option>
                                            <option value="night">Noche</option>
                                        </select>
                                    </div>
                                </div>

                                <h6 className="text-secondary border-bottom pb-2 mb-3">Condiciones Económicas (Por Turno)</h6>
                                <div className="row g-3">
                                    <div className="col-6 col-md-3">
                                        <label className="form-label small fw-bold">Valor Turno</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0">$</span>
                                            <input
                                                type="number"
                                                name="valorTurno"
                                                className="form-control border-start-0 ps-0"
                                                value={datosCondiciones.valorTurno}
                                                onChange={handleChangeCondiciones}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <label className="form-label small fw-bold">Colación</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0">$</span>
                                            <input
                                                type="number"
                                                name="valorColacion"
                                                className="form-control border-start-0 ps-0"
                                                value={datosCondiciones.valorColacion}
                                                onChange={handleChangeCondiciones}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <label className="form-label small fw-bold">Movilización</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0">$</span>
                                            <input
                                                type="number"
                                                name="valorMovilizacion"
                                                className="form-control border-start-0 ps-0"
                                                value={datosCondiciones.valorMovilizacion}
                                                onChange={handleChangeCondiciones}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <label className="form-label small fw-bold">Otro</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0">$</span>
                                            <input
                                                type="number"
                                                name="valorOtro"
                                                className="form-control border-start-0 ps-0"
                                                value={datosCondiciones.valorOtro}
                                                onChange={handleChangeCondiciones}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </form>
                </div>

                {/* Right Column: Summary Sticky */}
                <div className="col-lg-4">
                    <div className="card shadow-sm border-0 sticky-top" style={{ top: '20px', zIndex: 1 }}>
                        <div className="card-header bg-dark text-white py-3">
                            <h5 className="mb-0"><i className="fas fa-clipboard-list me-2"></i>Resumen</h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="mb-4">
                                <label className="d-block text-secondary small text-uppercase fw-bold mb-1">Paciente</label>
                                {pacienteInfo ? (
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                                            <i className="fas fa-user-injured"></i>
                                        </div>
                                        <div>
                                            <div className="fw-bold">{pacienteInfo.nombres} {pacienteInfo.apellidoPaterno}</div>
                                            <div className="small text-muted">{pacienteInfo.rut}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-muted fst-italic small">No seleccionado</div>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="d-block text-secondary small text-uppercase fw-bold mb-1">Auxiliar Reemplazado</label>
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                                        <i className="fas fa-user-nurse"></i>
                                    </div>
                                    <div className="text-truncate">
                                        {datosCondiciones.auxiliarID ? (
                                            // Since we might not have the full object for auxiliar dropdown, we try our best
                                            auxiliarInfo ? (
                                                <>
                                                    <div className="fw-bold text-truncate">{auxiliarInfo.nombre} {auxiliarInfo.apellidoPaterno}</div>
                                                    <div className="small text-muted">{auxiliarInfo.rut}</div>
                                                </>
                                            ) : (
                                                <div className="fw-bold text-dark">ID: {datosCondiciones.auxiliarID}</div>
                                            )
                                        ) : (
                                            <div className="text-muted fst-italic small">No seleccionado</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <hr />

                            <div className="mb-4">
                                <div className="row">
                                    <div className="col-6">
                                        <label className="text-secondary small fw-bold">Fecha Inicio</label>
                                        <div className="fw-bold">{datosCondiciones.fechaInicio ? datosCondiciones.fechaInicio.split('-').reverse().join('-') : '-'}</div>
                                        <div className="small text-muted">{datosCondiciones.turnoInicio === 'night' ? 'Noche' : 'Día'}</div>
                                    </div>
                                    <div className="col-6 text-end">
                                        <label className="text-secondary small fw-bold">Fecha Fin</label>
                                        <div className="fw-bold">{datosCondiciones.fechaFin ? datosCondiciones.fechaFin.split('-').reverse().join('-') : '-'}</div>
                                        <div className="small text-muted">{datosCondiciones.turnoFin === 'day' ? 'Día' : 'Noche'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-grid">
                                <button className="btn btn-primary btn-lg" onClick={handleSubmit}>
                                    Crear Solicitud <i className="fas fa-arrow-right ms-2"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SolicitudReemplazo2;
